import { setupOwnerSchema } from '@cf/shared';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';

import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/hooks';
import { AuthShell } from '../components/AuthShell';
import { Field, inputClass } from '../components/ui';

/** First run: create the OWNER account (only possible while none exists). */
export function SetupPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const status = useApi<{ needsOwner: boolean }>('/setup/status');
  const [form, setForm] = useState({ fullName: '', email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status.data && !status.data.needsOwner) return <Navigate to="/login" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = setupOwnerSchema.safeParse(form);
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? '');
    setBusy(true);
    setError(null);
    try {
      await api('/setup/owner', { method: 'POST', json: parsed.data });
      await login(parsed.data.email, form.password);
      await navigate('/configuration/company', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.genericError'));
    } finally {
      setBusy(false);
    }
  }

  const input = `${inputClass} h-11 text-base`;
  return (
    <AuthShell>
      <form onSubmit={(e) => void submit(e)} className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-stone-900">
            {t('setup.title')}
          </h1>
          <p className="mt-1.5 text-sm text-stone-500">{t('setup.intro')}</p>
        </div>
        <div className="space-y-4">
          <Field label={t('users.fullName')}>
            <input
              className={input}
              autoComplete="name"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
          </Field>
          <Field label={t('auth.email')}>
            <input
              className={input}
              type="email"
              autoComplete="username"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label={t('auth.password')} hint={t('users.passwordHint')}>
            <input
              className={input}
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Field>
        </div>
        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-700 ring-1 ring-inset ring-red-600/10">
            {error}
          </p>
        )}
        <button
          disabled={busy}
          className="h-11 w-full rounded-lg bg-copper-600 text-[15px] font-semibold text-on-fill shadow-xs ring-1 ring-inset ring-copper-700/20 transition-colors hover:bg-copper-700 disabled:opacity-60"
        >
          {t('setup.submit')}
        </button>
      </form>
    </AuthShell>
  );
}
